import { GetBlueSageTerminalPushQuery } from 'src/generated/graphql.generated';

export type BlueSagePushState = GetBlueSageTerminalPushQuery['blueSageTerminalPush'];

/**
 * What kind of push is on the device. Drives the copy, not the mechanics —
 * the transaction already knows which of these it is.
 */
export type BlueSagePushKind = 'sale' | 'auth' | 'ticket';

/**
 * Where a push is, from the operator's point of view.
 *
 * Deliberately NOT the wire status. `pending` on the wire covers both "we are
 * asking the middleware" and "the customer is holding the card", which are
 * completely different things to a person watching the screen — one is our
 * latency, the other is the customer's. Every state below says what is being
 * waited on, because "every wait state says what is being waited on" is the
 * rule this panel exists to satisfy.
 */
export type BlueSagePushPhase =
    /** Nothing on a device. */
    | 'idle'
    /**
     * The transaction is being written to the job. Nothing has been asked of
     * BlueSage yet, let alone of a device.
     *
     * It earns a phase of its own because `transactions.create` is not fast —
     * it writes the node, recomputes job totals and grants access — and on a
     * loaded instance that ran to about twenty seconds with nothing on screen
     * but a button spinner. An operator who has pressed Send and sees no panel
     * assumes the click was lost and presses it again.
     */
    | 'preparing'
    /** We are talking to the middleware; nothing is on the reader yet. */
    | 'sending'
    /** It is on the reader and the customer has it. */
    | 'waiting'
    /** Settled, money taken. */
    | 'settled'
    /** Settled, money HELD — an authorization is not a payment. */
    | 'authorized'
    | 'declined'
    | 'cancelled'
    /** The device never answered. Distinct from declined: nothing is known. */
    | 'timedOut'
    /**
     * The CRM lost track of the push: it has been in flight longer than any
     * request behind it could have lived. Nothing is known, the same as a
     * timeout, and the honest instruction is the same — check the device and
     * the review queue before sending again.
     */
    | 'lost'
    /** The push never left — an offline device, a rate limit, a bad amount. */
    | 'failed';

export const blueSageTerminalPushInitialState = {
    phase: 'idle' as BlueSagePushPhase,

    /** The transaction whose money this is. */
    transactionId: undefined as string | undefined,
    /** The device it went to, for the copy: *Waiting for the customer at ••7943*. */
    epi: undefined as string | undefined,
    kind: 'sale' as BlueSagePushKind,
    /** Minor units, so the panel can say the figure without re-reading the job. */
    amountMinor: undefined as number | undefined,

    /** BlueSage's id for the push. Polling and cancelling both need it. */
    pushId: undefined as string | undefined,

    /**
     * Epoch ms when the push reached the device.
     *
     * A push has a 330-second ceiling and a completion tip screen gives roughly
     * 30 seconds, so an operator coaching a customer down the phone needs to
     * know how long it has been — not a spinner.
     */
    startedAt: undefined as number | undefined,
    /** Ticks while a push is live, so the elapsed counter re-renders. */
    elapsedSeconds: 0 as number,

    /**
     * The reason, in the operator's language, already mapped from `body.code`.
     *
     * Never a raw code on screen. The code list is OPEN by contract, so the
     * server's `describePushFailure` default branch degrades gracefully and this
     * carries whatever it produced.
     */
    message: undefined as string | undefined,

    /** True while a cancel is in flight, so the button cannot be pressed twice. */
    cancelling: false as boolean,

    /**
     * The push never left, so the transaction created for it was cancelled.
     *
     * A push that is refused — a busy device, an offline one, an amount over the
     * location cap — has moved no money and touched no card, but the CRM had
     * already written a `pending` transaction to push AGAINST. Leaving that row
     * on the job is the defect YMM found on 2026-08-28: a $3.00 pending deposit
     * for a payment that never happened, indistinguishable from one still in
     * flight.
     *
     * When it is set, Try again is withdrawn: a cancelled transaction cannot be
     * pushed again, and the honest next step is to go back and send again.
     */
    transactionDiscarded: false as boolean,
};

export type BlueSageTerminalPushFeatureState = typeof blueSageTerminalPushInitialState;

/** The middleware's own ceiling. Past this the device has certainly let go. */
export const PUSH_CEILING_SECONDS = 330;

/**
 * Roughly how long the completion tip screen gives the customer.
 *
 * Measured, not configurable: a parameter download was tried on 2026-08-20 and
 * did not change it. It is shown as a countdown so the operator can coach
 * rather than guess.
 */
export const TIP_PROMPT_SECONDS = 30;
